import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ITooltipPortal {
	children: React.ReactNode;
	tooltip: string;
	position?: 'top' | 'bottom' | 'left' | 'right';
	size?: 'small' | 'normal';
	disabled?: boolean;
}

export const TooltipPortal: React.FC<ITooltipPortal> = (props) => {
	const { children, tooltip, position = 'right', size = 'normal', disabled = false } = props;
	const [show, setShow] = useState(false);
	const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
	const triggerRef = useRef<HTMLSpanElement>(null);

	const updatePosition = () => {
		if (!triggerRef.current) return;

		const rect = triggerRef.current.getBoundingClientRect();

		setCoords({
			top: rect.top + window.scrollY,
			left: rect.left + window.scrollX,
			width: rect.width,
			height: rect.height,
		});
	};

	useEffect(() => {
		if (show) {
			updatePosition();
			window.addEventListener('scroll', updatePosition);
			window.addEventListener('resize', updatePosition);

			return () => {
				window.removeEventListener('scroll', updatePosition);
				window.removeEventListener('resize', updatePosition);
			};
		}
	}, [show]);

	const handleMouseEnter = () => {
		if (!disabled) {
			setShow(true);
			updatePosition();
		}
	};

	const handleMouseLeave = () => {
		setShow(false);
	};

	return (
		<>
			<span
				ref={triggerRef}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onFocus={handleMouseEnter}
				onBlur={handleMouseLeave}
				style={{ display: 'inline-block' }}
			>
				{children}
			</span>
			{show &&
				!disabled &&
				createPortal(
					<span
						data-tooltip={tooltip}
						data-tooltip-position={position}
						data-tooltip-size={size}
						className="tooltip-portal-positioner"
						style={{
							position: 'absolute',
							top: coords.top,
							left: coords.left,
							width: coords.width,
							height: coords.height,
							pointerEvents: 'none',
						}}
					/>,
					document.body
				)}
		</>
	);
};
